import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom"; 
import { TemplateSub } from "../../components/layouts/TemplateSub";
import NewButton from "../../components/uiMerge/newButton";
import * as ROUTES from "../../constants/routes";
import { ViewWallet, ViewUser, MyAppSettingController } from "@njoy/controller";
import AntAvatar from "../shared/AntAvatar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ToastMessage from "src/hooks/useToast";

export const SendMoneyDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const [description, setDescription] = React.useState<string>("");
  const [rawAmount, setRawAmount] = React.useState<string>("");
  const [searchParams] = useSearchParams();
  // const customerId = searchParams.get("user"); 
  const userId = searchParams.get("user"); 
  const barterId = searchParams.get("barterId");

  return (
    <MyAppSettingController>
      {({data, loading}) => {

        if (loading || !data) {
          return <div>...loading</div>;
        }

        const topUpLimit = data.myAppSetting?.topUpLimit || 0;

        return(
        <TemplateSub page="Send Money">
          <ToastContainer />

          <div className="w-full flex-1 h-full overflow-y-auto pb-4">
            <div className="flex flex-col items-center mt-4 px-4">
              {/* Recipient Card */}
              <ViewUser userId={userId ?? ""}>
                {({ user, loading }) => (
                  <div className="w-full border-[0.5px] border-[#3a3b41] p-5 bg-[linear-gradient(180deg,#2A2844_-13.35%,#090A12_78.05%)] rounded-lg">
                    <div className="flex items-center gap-3 ">
                      <div className="w-[54px] h-[54px] flex justify-center items-center rounded-full overflow-hidden">
                        {user?.__typename === "AdminPWA" &&
                          (user.merchantProfile?.logoUrl ?
                            <img 
                              src={user.merchantProfile.logoUrl} 
                              alt="" 
                              className="w-full h-full object-cover"
                            /> : 
                            <AntAvatar 
                              firstname={user.merchantProfile?.companyName || ""}
                              width="71px" 
                              height="71px" 
                              fontSize="28px" 
                            />
                          )
                        }

                        {user?.__typename === "CustomerPWA" &&
                          <AntAvatar 
                            firstname={user?.firstname || ""} 
                            lastname={user?.lastname!} 
                            width="71px" 
                            height="71px" 
                            fontSize="28px"
                          />
                        }
                      </div>

                      <div className="flex flex-col">
                        {loading ? (
                          <p className="text-[22px] text-[#9fafb5] font-semibold">
                            Loading...
                          </p>
                        ) : (
                          <>
                            {user?.__typename === "AdminPWA" && 
                              <>
                                <p className="text-[22px] text-white opacity-80 font-semibold">
                                  {user.merchantProfile?.companyName}
                                </p>

                                <p className="text-[14px] text-[#777]">
                                  @{user.merchantProfile?.username}
                                </p>
                              </>
                            }

                            {user?.__typename === "CustomerPWA" && 
                              <>
                                <p className="text-[22px] text-white opacity-80 font-semibold">
                                  {user.firstname}{" "} {user.lastname}
                                </p>

                                <p className="text-[12px] text-[#777] ">@{user.username}</p>
                              </>
                            }
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </ViewUser>

              {/* Amount Input */}
              <div className="w-full mt-7 mb-8 justify-center flex flex-col items-center">
                <input
                  type="text"
                  value={
                    rawAmount
                      ? `₦${rawAmount.replace(/[^\d]/g, "").replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")}`
                      : ""
                  }
                  onChange={(e) => {
                    let value = e.target.value.replace(/^₦/, "").replace(/,/g, "");
                    // Allow only whole numbers
                    value = value.replace(/[^\d]/g, "");
                    setRawAmount(value);
                  }}
                  placeholder="₦ 0.00"
                  className="w-full p-2 mt-2 text-white text-center text-2xl font-medium bg-transparent placeholder:text-[#777] input-placeholder"
                />
                <hr className="bg-[#77777] mt-1 border w-full border-[#7777]" />

                <ViewWallet>
                  {({ match, loading }) => (
                    <>
                      {loading ? (
                        <p className="text-[10px] text-white mt-1">Loading...</p>
                      ) : (
                        <p className="text-[15px] text-white mt-1">
                          <span className="text-[#777] mr-2">Balance</span>&#8358;{" "}
                          {match?.balance?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      )}
                    </>
                  )}
                </ViewWallet>

                {/* Description */}
                <div className="w-full mt-4">
                  <label className="block text-[12px] font-medium text-[#777] mb-1">
                    Description
                  </label>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full p-2 bg-[#1E1E1E] rounded-lg text-sm placeholder:text-[#777] text-white h-[50px]"
                    placeholder="Add a note (optional)"
                  />
                </div>

                {/* Send Button */}
                <NewButton
                  fullWidth
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    const amount = parseInt(rawAmount);
                
                    if(amount > topUpLimit) {
                      ToastMessage("error", "Transaction amount exceeds top-up limit")
                      return
                    }
                    
                    if(amount < 500) {
                      ToastMessage("error", "Minimum NGN 500 transaction")
                      return
                    }

                    if(rawAmount !== "" && barterId && amount > 0) {
                      localStorage.setItem("barterId", barterId);
                      localStorage.setItem("amount", rawAmount);
                      localStorage.setItem("narration", description);
                      localStorage.setItem("currency", "NGN")
                      navigate(ROUTES.VIEW_WALLET_CONFIRM_TRANSACTION)
                    } else {
                      ToastMessage("error", "Enter a valid amount")
                    }
                  }}
                  className="w-full mb-8 px-4 mt-14 cursor-pointer bg-gradient-to-l from-[#A151F9] to-[#0045E9] text-white"
                >
                  <p className="text-[16px] text-center">Send</p>
                </NewButton>
              </div>
            </div>
          </div>
        </TemplateSub>
        )
      }}
    </MyAppSettingController>
  );
};
